import { Form, Field } from 'react-final-form';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Card, Elevation, Button } from '@blueprintjs/core';
import selectors from 'src/selectors';
import { IRootState } from 'src/reducers';
import { connect } from 'react-redux';
import actions from 'src/actions';
import './ManageContactInfoPage.scss';
import ManageContactInfo from '../../../models/forms/ManageContactInfo';
import Landman from '../../../models/Landman';
import State from '../../../models/State';
import CardContainer from '../../common/CardContainer';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import { FormApi, FieldState, AnyObject } from 'final-form';
import PagedResultSet from 'src/models/common/PagedResultSet';
import { ODataOptions } from 'src/util/ODataBuilder';
import { SelectItem } from 'src/models/common/SelectItem';
import PropertyUtil from 'src/util/PropertyUtil';
import ManageContactInfoChanges from 'src/models/forms/ManageContactInfoChanges';
import PhoneNumberType from 'src/models/PhoneNumberType';
import { FormErrors } from 'src/util/FormErrors';
import PhoneNumber from 'src/models/PhoneNumber';
import FormTextBox, { FormTextBoxDataType } from '../../controls/forms/redesign/FormTextbox';
import FormSelect from '../../controls/forms/redesign/FormFilterSelect';
import FormCheckbox from '../../controls/forms/redesign/FormCheckbox';
import FormButton from '../../controls/forms/redesign/FormButton';
import FormRow from '../../controls/forms/FormRow';

export type IManageContactInfoPageStateProps = {
    landman: (id: number) => Landman;
    states: PagedResultSet<State>;
    phoneNumberTypes: Array<PhoneNumberType>;
    errorMessage: string;
};

export interface IManageContactInfoPageDispatchProps {
    getLandman: (id: number) => void;
    getPhoneNumberTypes: () => void;
    getStates: (options: ODataOptions) => void;
    saveLandmanContactInfo: (formValues: ManageContactInfo) => void;
    saveLandmanContactInfoChanges: (landmanId: number, formValues: ManageContactInfoChanges) => void;
}

type IManagePageProps = RouteComponentProps<{ id?: string }> & IManageContactInfoPageStateProps & IManageContactInfoPageDispatchProps;

class ManageContactInfoPage extends React.Component<IManagePageProps> {
    private manageContactInfoChanges = new ManageContactInfoChanges();
    // This flag is used because removing a phone number from the FinalForm FieldArray doesn't cause the form to register as dirty
    private phoneNumberListPristine = true;
    private FORMATTED_PHONE_NUMBER_LENGTH = 14;

    constructor(props: IManagePageProps) {
        super(props);
        this.state = { primaryPhoneNumberOption: 0 };

        this.handleSubmit = this.handleSubmit.bind(this);
    }

    componentDidMount() {
        this.props.getLandman(parseInt(this.props.match.params.id, 10));
        this.props.getPhoneNumberTypes();
        this.props.getStates(new ODataOptions().addPaging(1, 100));
    }

    handleSubmit = (values: ManageContactInfo, form: FormApi) => {
        if (
            values.sameAsPrimaryCHK ||
            (values.secondary.AddressLine1 == null &&
                values.secondary.AddressLine2 == null &&
                values.secondary.City == null &&
                values.secondary.ZipCode == null)
        ) {
            delete values.secondary;
        }

        const dirtyPhoneNumberRecords = this.getDirtyPhoneRecords(values, form);
        if (dirtyPhoneNumberRecords) {
            this.manageContactInfoChanges.phoneNumberUpdates = Array.from(new Set(dirtyPhoneNumberRecords)).filter(
                (phoneRecord) => phoneRecord && phoneRecord.id !== undefined
            );
            this.manageContactInfoChanges.phoneNumberAdds = Array.from(new Set(dirtyPhoneNumberRecords)).filter(
                (phoneRecord) => phoneRecord && phoneRecord.id === undefined
            );
        }

        const landmanId = parseInt(this.props.match.params.id, 10);
        this.props.saveLandmanContactInfoChanges(landmanId, this.manageContactInfoChanges);
        this.props.saveLandmanContactInfo(values);
    };

    private getDirtyPhoneRecords = (values: ManageContactInfo, form: FormApi) => {
        // tslint:disable-next-line:no-any
        const formDirtyFields = Object.keys((form.getState() as any).dirtyFields);
        const dirtyPhoneRecords = formDirtyFields.filter((d) => d.includes('phoneNumber'));
        const regex = new RegExp('[0-9]');

        return dirtyPhoneRecords.map((dirtyField) => {
            if (String(dirtyField).match(regex)) {
                const dirtyRecordIndex = String(dirtyField).match(regex)[0];
                return (
                    // tslint:disable-next-line:no-string-literal
                    values['phoneNumberList'][dirtyRecordIndex]
                );
            }
            return (
                // tslint:disable-next-line:no-string-literal
                undefined
            );
        });
    };

    onCancel = (form: FormApi) => {
        form.reset();
        this.phoneNumberListPristine = true;
    };

    // tslint:disable-next-line:no-any
    handlePhoneNumberRemove = (form: FormApi, index: number, fields: { remove: (index: number) => any } & FieldState<AnyObject>) => {
        // tslint:disable-next-line:no-string-literal
        const phoneRecordId = PropertyUtil.SafeGet(() => (form.getState().values as ManageContactInfo).phoneNumberList[index]['id']);

        if (phoneRecordId !== undefined) {
            this.manageContactInfoChanges.phoneNumberDeleteIds.push(phoneRecordId);
            this.phoneNumberListPristine = false;
        }
        fields.remove(index);
    };

    handlePrimaryPhoneNumberChange = (form: FormApi, index: number) => {
        const records = (form.getState().values as ManageContactInfo).phoneNumberList.length;
        for (let i = 0; i < records; i++) {
            form.change(`phoneNumberList[${i}].isPrimary`, i === index);
        }
    };

    handlePhoneNumberTypeChange = (form: FormApi, index: number, phoneNumberTypeOffice: string) => {
        // tslint:disable-next-line:no-string-literal
        if (
            PropertyUtil.SafeGet(
                () => (form.getState().values as ManageContactInfo).phoneNumberList[index].typeId.toString() !== phoneNumberTypeOffice
            )
        ) {
            console.log('deleting the extension if it exists');
            form.change(`phoneNumberList[${index}].extension`, undefined);
        }
    };

    validateAddress = (values: ManageContactInfo) => {
        const fiveDigitZipCode = /(^\d{5}$)/;

        const errors: AnyObject = {};
        errors.primary = {};
        errors.primary.State = {};
        errors.secondary = {};
        errors.secondary.State = {};

        // Primary Address
        if (values.primary.AddressLine1 === undefined) {
            errors.primary.AddressLine1 = 'Address required';
        }

        if (values.primary.City === undefined) {
            errors.primary.City = 'City required';
        }

        if (values.primary.State === undefined) {
            errors.primary.State.id = 'State required';
        }

        if (values.primary.ZipCode === undefined || values.primary.ZipCode.match(fiveDigitZipCode) === null) {
            errors.primary.ZipCode = '5 Digit Zip code required';
        }

        // Mailing/Shipping Address
        if (values.sameAsPrimaryCHK === false) {
            if (values.secondary.AddressLine1 === undefined) {
                errors.secondary.AddressLine1 = 'Address required';
            }

            if (values.secondary.City === undefined) {
                errors.secondary.City = 'City required';
            }

            if (values.secondary.State === undefined) {
                errors.secondary.State.id = 'State required';
            }

            if (values.secondary.ZipCode === undefined || values.secondary.ZipCode.match(fiveDigitZipCode) === null) {
                errors.secondary.ZipCode = '5 digit Zip code required';
            }
        }

        return errors;
    };

    validatePhoneNumbers = (phoneNumberList: AnyObject) => {
        const errorList: Array<FormErrors<PhoneNumber>> = [];

        const isPhoneNumberEmpty = (index: number) =>
            phoneNumberList[index] === undefined || phoneNumberList[index].number === undefined || phoneNumberList[index].number === '';

        const isPhoneTypeEmpty = (index: number) =>
            phoneNumberList[index] === undefined || phoneNumberList[index].phoneNumberType === undefined || phoneNumberList[index].phoneNumberType === '';

        const isPhoneNumberTenDigits = (index: number) => {
            return (
                phoneNumberList[index] &&
                phoneNumberList[index].number &&
                phoneNumberList[index].number.toString().length === this.FORMATTED_PHONE_NUMBER_LENGTH
            );
        };
        const isPhoneNumberExtensionNotNumeric = (index: number) =>
            phoneNumberList[index] && phoneNumberList[index].extension && !phoneNumberList[index].extension.toString().match(/^[0-9]*$/);
        const isPhoneNumberExtensionNotEmpty = (index: number) =>
            phoneNumberList[index] && !(phoneNumberList[index].extension === undefined || phoneNumberList[index].extension === '');
        const isPhoneNumberExtensionTooLong = (index: number) =>
            phoneNumberList[index] && phoneNumberList[index].extension && phoneNumberList[index].extension.toString().length > 10;

        for (let i = 0; i < phoneNumberList.length; i++) {
            const errors: FormErrors<PhoneNumber> = {};

            if (isPhoneNumberEmpty(i)) {
                errors.number = 'Phone number required';
            }

            if (isPhoneTypeEmpty(i)) {
                errors.number = 'Phone Type required';
            }

            if (!isPhoneNumberTenDigits(i)) {
                errors.number = '10 digit phone number required';
            }

            if (isPhoneNumberExtensionNotNumeric(i) && isPhoneNumberExtensionNotEmpty(i)) {
                errors.extension = 'Digits only for extension';
            }

            if (isPhoneNumberExtensionTooLong(i)) {
                errors.extension = 'Max of 10 digits for extension';
            }
            if (Object.keys(errors).length) {
                errorList[i] = errors;
            }
        }
        return errorList;
    };

    public render() {
        const landman = this.props.landman(parseInt(this.props.match.params.id, 10));

        const phoneNumberTypes = PropertyUtil.SafeGet(() => this.props.phoneNumberTypes);
        const selectPhoneNumberTypes = phoneNumberTypes
            ? phoneNumberTypes.map((phoneNumberType: PhoneNumberType) => new SelectItem(phoneNumberType.id, phoneNumberType.type))
            : undefined;
        const phoneNumberTypeOffice = PropertyUtil.SafeGet(() =>
            phoneNumberTypes.find((phoneNumbertype: PhoneNumberType) => phoneNumbertype.type === 'Office').id.toString()
        );

        const states = PropertyUtil.SafeGet(() => this.props.states.items);
        const selectStates = states ? states.map((state: State) => new SelectItem(state.id, state.name)) : undefined;

        return (
            <CardContainer>
                <Card elevation={Elevation.THREE} className="form-card">
                    <h2>Manage Landman Contact Info</h2>
                    <Form
                        onSubmit={this.handleSubmit}
                        mutators={{
                            ...arrayMutators,
                        }}
                        validate={this.validateAddress}
                        initialValues={{
                            sameAsPrimaryCHK:
                                (landman && landman.primaryAddress ? landman.primaryAddress.id : 1) ===
                                (landman && landman.mailingAddress ? landman.mailingAddress.id : 2),
                            primary: {
                                Id: landman && landman.primaryAddress ? landman.primaryAddress.id : undefined,
                                AddressLine1: landman && landman.primaryAddress ? landman.primaryAddress.addressLine1 : undefined,
                                AddressLine2: landman && landman.primaryAddress ? landman.primaryAddress.addressLine2 : undefined,
                                City: landman && landman.primaryAddress ? landman.primaryAddress.city : undefined,
                                State: {
                                    id: landman && landman.primaryAddress ? landman.primaryAddress.state.id : undefined,
                                },
                                ZipCode: landman && landman.primaryAddress ? landman.primaryAddress.zipCode : undefined,
                            },
                            secondary: {
                                Id: landman && landman.mailingAddress ? landman.mailingAddress.id : undefined,
                                AddressLine1: landman && landman.mailingAddress ? landman.mailingAddress.addressLine1 : undefined,
                                AddressLine2: landman && landman.mailingAddress ? landman.mailingAddress.addressLine2 : undefined,
                                City: landman && landman.mailingAddress ? landman.mailingAddress.city : undefined,
                                State: {
                                    id: landman && landman.mailingAddress ? landman.mailingAddress.state.id : undefined,
                                },
                                ZipCode: landman && landman.mailingAddress ? landman.mailingAddress.zipCode : undefined,
                            },
                            landmanId: parseInt(this.props.match.params.id, 10),
                            phoneNumberList: landman && landman.phoneNumberList ? landman.phoneNumberList : [],
                        }}
                        render={({ handleSubmit, form, submitting, pristine, values }) => {
                            // Adds an empty record to the PhoneNumber list FieldArray if no records exist to prevent the user from having to push the "Add Phone Number" button
                            // to enter the first phone number.
                            if (landman && (landman.phoneNumberList === undefined || landman.phoneNumberList.length === 0)) {
                                landman.phoneNumberList.push({ id: undefined, number: '', extension: undefined, typeId: undefined, isPrimary: true });
                            }
                            return (
                                <form onSubmit={handleSubmit}>
                                    <div className="address-div">
                                        <div className="primary-div">
                                            <label className="sub-form-header">Primary Address:</label>
                                            <FormTextBox label="Address Line 1" name="primary.AddressLine1" isRequired={true} />
                                            <FormTextBox label="Address Line 2" name="primary.AddressLine2" />
                                            <FormRow>
                                                <FormTextBox label="City" name="primary.City" isRequired={true} />
                                                <FormSelect label="State" name="primary.State.id" items={selectStates} isRequired={true} />
                                                <FormTextBox label="Zip Code" name="primary.ZipCode" isRequired={true} />
                                            </FormRow>
                                        </div>
                                        <div className="mailing-div">
                                            <label className="sub-form-header">Mailing/Shipping Address:</label>
                                            <div>
                                                <label className="bp3-control bp3-switch">
                                                    <Field name="sameAsPrimaryCHK" component="input" type="checkbox" />
                                                    <span className="bp3-control-indicator" />
                                                    Same as Primary
                                                </label>
                                            </div>
                                            {!(values as ManageContactInfo).sameAsPrimaryCHK && [
                                                <div key="1">
                                                    <FormTextBox label="Address Line 1" name="secondary.AddressLine1" isRequired={true} />
                                                    <FormTextBox label="Address Line 2" name="secondary.AddressLine2" />
                                                    <FormRow>
                                                        <FormTextBox label="City" name="secondary.City" isRequired={true} />
                                                        <FormSelect label="State" name="secondary.State.id" items={selectStates} isRequired={true} />
                                                        <FormTextBox label="Zip Code" name="secondary.ZipCode" isRequired={true} />
                                                    </FormRow>
                                                </div>,
                                            ]}
                                        </div>
                                    </div>
                                    <div className="phone-div">
                                        <h4>Phone Number List:</h4>
                                        <FieldArray name="phoneNumberList" validate={this.validatePhoneNumbers}>
                                            {({ fields, meta }) =>
                                                fields.map((name, index) => (
                                                    <div key={name} className="phone-record">
                                                        <FormRow>
                                                            <FormCheckbox
                                                                name={`${name}.isPrimary`}
                                                                label="Primary"
                                                                onChange={() => this.handlePrimaryPhoneNumberChange(form, index)}
                                                            />
                                                            <FormTextBox
                                                                name={`${name}.number`}
                                                                label="Number"
                                                                type={FormTextBoxDataType.PhoneNumber}
                                                                isRequired={true}
                                                            />
                                                            {/* Conditional rendering of Phone Number Extension Field if Phone Number Type is Office*/}
                                                            {PropertyUtil.SafeGet(
                                                                () =>
                                                                    (values as ManageContactInfo).phoneNumberList[index].typeId.toString() ===
                                                                    phoneNumberTypeOffice
                                                            ) && <FormTextBox name={`${name}.extension`} label="Ext." />}
                                                            <FormSelect
                                                                name={`${name}.typeId`}
                                                                label="Phone Type"
                                                                items={selectPhoneNumberTypes}
                                                                onChange={() => this.handlePhoneNumberTypeChange(form, index, phoneNumberTypeOffice)}
                                                                isRequired={true}
                                                            />
                                                            <span
                                                                className="remove-phone"
                                                                onClick={() => this.handlePhoneNumberRemove(form, index, fields)}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                ❌
                                                            </span>
                                                        </FormRow>
                                                    </div>
                                                ))
                                            }
                                        </FieldArray>
                                        <Button
                                            className="bp3-small bp3-icon-add"
                                            onClick={() => form.mutators.push('phoneNumberList')}
                                            text="Add Phone Number"
                                            intent="success"
                                        />
                                    </div>
                                    <div className="buttons">
                                        <FormButton
                                            text="Save"
                                            type="submit"
                                            disabled={submitting || (pristine && this.phoneNumberListPristine)}
                                            style={{ margin: '.5em' }}
                                        />
                                        <FormButton
                                            text="Cancel"
                                            type="button"
                                            onClick={() => this.onCancel(form)}
                                            disabled={submitting || (pristine && this.phoneNumberListPristine)}
                                        />
                                    </div>
                                </form>
                            );
                        }}
                    />
                </Card>
            </CardContainer>
        );
    }
}

const mapStateToProps = (state: IRootState) => ({
    landman: (id: number) => selectors.landman.landman(id)(state),
    phoneNumberTypes: selectors.phoneNumberType.phoneNumberTypes(state),
    states: selectors.state.states(state),
});

const mapDispatchToProps: IManageContactInfoPageDispatchProps = {
    getLandman: (id: number) => actions.landman.getLandman(id),
    getPhoneNumberTypes: () => actions.phoneNumberType.getPhoneNumberTypes(),
    saveLandmanContactInfo: (formValues: ManageContactInfo) => actions.landman.saveLandmanContactInfo(formValues),
    getStates: (options: ODataOptions) => actions.state.getStates(options),
    saveLandmanContactInfoChanges: (landmanId: number, formValues: ManageContactInfoChanges) =>
        actions.landman.saveLandmanContactInfoChanges(landmanId, formValues),
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageContactInfoPage);



// WEBPACK FOOTER //
// ./src/components/personnel/pages/ManageContactInfoPage.tsx